import React, { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper
} from '@mui/material';
import { getDadosCadastroEndereco, listarUltimaPPC } from 'api/onboarding';
import { validarSeDocumentoFoiAtualizado } from 'api/clientSearch';

import { getClienteAtual } from 'shared';
import LoaderMain from '../../Onboarding/shared/LoaderMain';
import ButtonMain from '../../Onboarding/shared/ButtonMain';
import Detalhes from '../Detalhes';

export default function DocumentosEmpresa({
  dadosDocumentosEmpresa,
  detalhesDocumentos,
  setDetalhesDocumentos,
  iconeStatus,
  listaTiposDocumentos,
  clienteSelecionado = false,
  cnpjEmpresa = false,
  isPending,
  carregarAbaEmpresa,
  bloquearTipoDocDuplicado = true,
  arquivosAceitos = ['pdf']
}) {
  const [listaDocs, setListaDocs] = useState([]);
  const [enderecoCliente, setEnderecoCliente] = useState({ uf: '' });
  const [loadingEndereco, setLoadingEndereco] = useState(false);
  const [documentosAtualizados, setDocumentosAtualizados] = useState(0);
  const [buscarNovamenteInformacoes, setBuscarNovamenteInformacoes] = useState(false);
  const [estadoUltimaPPC, setEstadoUltimaPPC] = useState('');
  const clienteAtual = getClienteAtual();

  useEffect(() => {
    if (dadosDocumentosEmpresa && Object.keys(dadosDocumentosEmpresa).length > 0) {
      setListaDocs(dadosDocumentosEmpresa.documentos || []);
    }
  }, [dadosDocumentosEmpresa]);

  useEffect(() => {
    if (clienteSelecionado && clienteSelecionado.cnpj) {
      listarUltimaPPC(clienteSelecionado.cnpj)
        .then(response => {
          const data = response.data;
          setEstadoUltimaPPC(data.ESTADOPPC || '');
        })
        .catch(() => {
          setEstadoUltimaPPC('');
        });

      validarSeDocumentoFoiAtualizado(clienteSelecionado.cnpj).then(response => {
        setDocumentosAtualizados(response.data.qtde);
      });
    }
  }, [clienteSelecionado, buscarNovamenteInformacoes]);

  // Busca endereço (UF) do cliente
  const fetchEndereco = useCallback(async () => {
    if (!clienteSelecionado || !clienteSelecionado.cnpj) return;
    try {
      setLoadingEndereco(true);
      const resp = await getDadosCadastroEndereco(clienteSelecionado.cnpj);
      const uf =
        (resp && resp.data && resp.data.endereco && resp.data.endereco.uf) ||
        (resp && resp.data && resp.data.uf) ||
        '';
      setEnderecoCliente({ uf: String(uf || '').toUpperCase() });
    } catch (e) {
      setEnderecoCliente({ uf: '' });
    } finally {
      setLoadingEndereco(false);
    }
  }, [clienteSelecionado ? clienteSelecionado.cnpj : null]);

  useEffect(() => {
    fetchEndereco();
  }, [fetchEndereco]);

  // Tipos que você quer iniciar
  const DOCS_INICIAIS = [2, 3, 14, 68, 17, 9, 69, 1, 16];

  // Monta a lista levando em conta UF + renomeios/duplicações
  const montarListaDocsInicial = (listaTipos, endereco) => {
    listaTipos = Array.isArray(listaTipos) ? listaTipos : [];
    endereco = endereco || {};

    const porCodigo = new Map(
      listaTipos.map(d => {
        return [d.CODPPCDOCUMENTO, d];
      })
    );

    const lista = [];
    let seq = 0;

    const uf = String((endereco && endereco.uf) || '').toUpperCase();
    const deveIncluir68 = uf !== 'SP'; // só inclui 68 se UF for diferente de SP

    DOCS_INICIAIS.forEach(cod => {
      // pular o 68 quando uf === 'SP'
      if (cod === 68 && !deveIncluir68) return;

      const base = porCodigo.get(cod) || {};
      const baseNome = String(base.NOME || '')
        .toUpperCase()
        .trim();
      const isEndividamento = cod === 3 || baseNome === 'ENDIVIDAMENTO';
      const isSacados = cod === 14;
      const isRJ = cod === 17 || baseNome === 'RECUPERAÇÃO JUDICIAL';
      const isFrota = cod === 9 || baseNome === 'RELAÇÃO DE FROTA';

      if (cod === 2) {
        // FATURAMENTO → 3 itens com observação
        const OBS_FAT = [
          'FATURAMENTO DOS ÚLTIMOS 12 MESES',
          'FATURAMENTO DOS ÚLTIMOS 24 MESES',
          'FATURAMENTO DOS ULTIMOS 36 MESES'
        ];
        OBS_FAT.forEach(obs => {
          lista.push({
            codigo: cod,
            id: 'doc_' + cod + '_' + seq++,
            cod_documento: cod,
            nome_doc: base.NOME || 'FATURAMENTO',
            explicacao_doc: base.DESCRICAO || '',
            observacao_doc: obs,
            obrigacao_doc: false,
            pareceres: [],
            status: 'reprovado'
          });
        });
      } else if (cod === 69) {
        // BALANÇO/DRE → duplicar em 2 itens com a observação solicitada
        const OBS_BAL = 'Balancete/DRE do ano corrente e balanço/DRE do ano anterior';
        [1, 2].forEach(() => {
          lista.push({
            codigo: cod,
            id: 'doc_' + cod + '_' + seq++,
            cod_documento: cod,
            nome_doc: base.NOME || 'BALANÇO/DRE',
            explicacao_doc: base.DESCRICAO || '', // mantém descrição original se houver
            observacao_doc: OBS_BAL,
            obrigacao_doc: false,
            pareceres: [],
            status: 'reprovado'
          });
        });
      } else {
        // Demais tipos (1 item), aplicando renomeios e observações específicas
        lista.push({
          codigo: cod,
          id: 'doc_' + cod + '_' + seq++,
          cod_documento: cod,
          nome_doc: isEndividamento
            ? 'ENDIVIDAMENTO BANCOS E FIDCS'
            : isSacados
            ? 'RELAÇÃO DE SACADOS OU CURVA ABC'
            : isRJ
            ? 'EMPRESAS EM RJ'
            : base.NOME || '',
          // se for RJ, sobrescreve a descrição
          explicacao_doc: isRJ ? 'deve ser incluído se estiver em RJ' : base.DESCRICAO || '',
          // se for RJ, mantém observação igual à descrição; se for FROTA, aplica observação solicitada
          observacao_doc: isRJ
            ? 'deve ser incluído se estiver em RJ'
            : isFrota
            ? 'deve ser incluído se for do ramo de transporte.'
            : base.observacao_doc || '',
          obrigacao_doc: false,
          pareceres: [],
          status: 'reprovado'
        });
      }
    });

    return lista;
  };

  const ordenarDocsReprovadoPrimeiro = (docs = []) => {
    const arr = Array.isArray(docs) ? docs : [];
    return arr.slice().sort((a, b) => {
      const ap =
        String(a.titulo_status ? a.titulo_status : '')
          .trim()
          .toUpperCase() === 'REPROVADO'
          ? 0
          : 1;
      const bp =
        String(b.titulo_status ? b.titulo_status : '')
          .trim()
          .toUpperCase() === 'REPROVADO'
          ? 0
          : 1;
      if (ap !== bp) return ap - bp; // REPROVADO primeiro
      return String(a.titulo ? a.titulo : '').localeCompare(String(b.titulo ? b.titulo : ''));
    });
  };

  const verDocumentos = async () => {
    if (!enderecoCliente || !enderecoCliente.uf) {
      await fetchEndereco();
    }

    const ordenada = ordenarDocsReprovadoPrimeiro(listaDocs);
    setListaDocs(ordenada);

    // const qtdReprov = Number(clienteAtual && clienteAtual.qtd_documento_reprovado) || 0;
    const docsAtual = Number(documentosAtualizados) || 0;
    const limVenc = clienteAtual && clienteAtual.limiteVencendo;
    const isFinal = ['FIM', 'INA'].includes(estadoUltimaPPC);

    if (docsAtual === 0 && limVenc === 'S' && isFinal) {
      flushSync(() => {
        const listaVaziaComTipos = montarListaDocsInicial(listaTiposDocumentos, enderecoCliente);
        setListaDocs(listaVaziaComTipos);
      });

      // 3) Demais casos → ordenada
    } else {
      setListaDocs(ordenada);
    }

    setDetalhesDocumentos(true);
  };

  const fecharDocumentos = () => {
    setDetalhesDocumentos(false);
    setBuscarNovamenteInformacoes(!buscarNovamenteInformacoes);
  };

  let estadoLimiteVencido =
    documentosAtualizados === 0 &&
    clienteSelecionado.limiteVencendo === 'S' &&
    ['FIM', 'INA'].includes(estadoUltimaPPC);

  return (
    <Box sx={isPending ? { position: 'relative' } : {}}>
      {isPending && <LoaderMain tipoLoader="float" tipoImagem="circulo" />}
      {detalhesDocumentos === false ? (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
            <TableHead className="tbl-head-doc-clientes">
              <TableRow>
                <TableCell align="left" className="texto-semi-negrito">
                  EMPRESA
                </TableCell>
                <TableCell align="left" className="texto-semi-negrito">
                  TIPO DE EMPRESA
                </TableCell>
                <TableCell align="left" sx={{ minWidth: '170px' }} className="texto-semi-negrito">
                  STATUS
                </TableCell>
                <TableCell align="left" sx={{ width: '270px' }} />
              </TableRow>
            </TableHead>
            <TableBody className="tab-body-doc-clientes">
              {dadosDocumentosEmpresa && dadosDocumentosEmpresa.dados ? (
                <TableRow>
                  <TableCell align="left">{dadosDocumentosEmpresa.dados.nome}</TableCell>
                  <TableCell align="left">{dadosDocumentosEmpresa.dados.tipo_empresa}</TableCell>
                  <TableCell align="left">
                    {iconeStatus[dadosDocumentosEmpresa.dados.status_documentos]}
                    {dadosDocumentosEmpresa.dados.status_documentos}
                  </TableCell>
                  <TableCell align="left">
                    <ButtonMain
                      tipoBotao="principal"
                      onClick={() => verDocumentos()}
                      sx={{ m: '10px 0' }}
                      disabled={loadingEndereco}
                    >
                      VER DOCUMENTOS
                      {(dadosDocumentosEmpresa.dados.status_documentos === 'REPROVADO' &&
                        !['FIM', 'INA'].includes(estadoUltimaPPC)) ||
                      estadoLimiteVencido ? (
                        <div className="icone-alert-right-top" />
                      ) : null}
                    </ButtonMain>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={4}>
                    Nenhum Registro Encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Detalhes
          tipo="empresa"
          estadoUltimaPPC={estadoUltimaPPC}
          clienteSelecionado={clienteSelecionado}
          cnpjEmpresa={cnpjEmpresa}
          listaDocumentos={listaDocs}
          setListaDocumentos={setListaDocs}
          fecharDocumentos={() => fecharDocumentos()}
          dados={dadosDocumentosEmpresa.dados}
          listaTiposDocumentos={listaTiposDocumentos}
          carregarAba={carregarAbaEmpresa}
          bloquearTipoDocDuplicado={bloquearTipoDocDuplicado}
          arquivosAceitos={arquivosAceitos}
          qtdDocumentosAtualizados={documentosAtualizados}
        />
      )}
    </Box>
  );
}
